/**
 * Local types for the internal activity endpoints of user-api
 * (`getOrgActivity`, `getOrgActivityHeatmap`, `getOrgActivityByHour`, `getPlatformActivitySummary`).
 *
 * TODO: replace with `UserPaths.*` / `UserComponents.Schemas.*` from `@epilot/internal-user-client`
 * once the client is republished (0.7.0) with the activity operations.
 */
export type ActivityGranularity = 'day' | 'week' | 'month'

export const ACTIVITY_GRANULARITIES: ActivityGranularity[] = [
  'day',
  'week',
  'month'
]

export interface OrgActivitySummary {
  members: number
  billable_members: number
  hubspot_company_id?: string | null
  /** licensed seats from HubSpot `no__of_seats`, null if unknown */
  paid_seats: number | null
  /** active_30d / paid_seats, null without paid seats. Can exceed 1. */
  activation_rate_30d: number | null
  /** active_30d / members, null without members */
  member_activation_rate_30d: number | null
  /** seen within the last 15 minutes */
  active_now: number
  active_today: number
  active_7d: number
  active_prev_7d: number
  active_30d: number
  active_prev_30d: number
  dormant_30d: number
  never_seen: number
}

export interface ActivitySeriesPoint {
  /** first day of the period (Monday for weeks, 1st for months), `YYYY-MM-DD` */
  period_start: string
  active_users: number
}

export interface OrgActivityTopUser {
  user_id: string
  name: string | null
  email: string | null
  active_days: number
  last_seen_at: string | null
}

export interface OrgActivityAttentionUser {
  user_id: string
  name: string | null
  email: string | null
  status: string | null
  created_at: string | null
  last_login_at: string | null
  last_seen_at: string | null
  reason: 'dormant' | 'never_seen'
}

export interface OrgActivity {
  org_id: string
  generated_at: string
  granularity: ActivityGranularity
  from: string
  to: string
  summary: OrgActivitySummary
  series: ActivitySeriesPoint[]
  top_users: OrgActivityTopUser[]
  attention: OrgActivityAttentionUser[]
}

export interface GetOrgActivityParams {
  granularity?: ActivityGranularity
  from?: string
  to?: string
  top_limit?: number
}

export interface ActivityHeatmapDay {
  day: string
  /** distinct active users (org) or 1 = active (single user) */
  value: number
}

export interface OrgActivityHeatmap {
  org_id: string
  user_id: string | null
  from: string
  to: string
  /** only days with value > 0 */
  days: ActivityHeatmapDay[]
}

export interface GetOrgActivityHeatmapParams {
  from?: string
  to?: string
  user_id?: string
}

export interface ActivityHourCell {
  /** ISO weekday, 1 = Monday ... 7 = Sunday */
  weekday: number
  hour: number
  avg_active_users: number
}

export interface OrgActivityByHour {
  org_id: string
  from: string
  to: string
  /** only non-zero cells */
  cells: ActivityHourCell[]
}

export type PlatformActivitySort = 'mau' | 'wau' | 'activation_rate'
export type SortOrder = 'asc' | 'desc'

export interface PlatformActivitySeriesPoint extends ActivitySeriesPoint {
  active_orgs: number
}

export interface PlatformActivityOrg {
  org_id: string
  name: string | null
  mau: number
  wau: number
  members: number
  paid_seats: number | null
  activation_rate_30d: number | null
}

export interface PlatformActivitySummary {
  generated_at: string
  granularity: ActivityGranularity
  from: string
  to: string
  summary: {
    dau: number
    dau_prev: number
    wau: number
    wau_prev: number
    mau: number
    mau_prev: number
    active_now: number
    active_orgs_30d: number
  }
  series: PlatformActivitySeriesPoint[]
  orgs: PlatformActivityOrg[]
}

export interface GetPlatformActivityParams {
  granularity?: ActivityGranularity
  from?: string
  to?: string
  org_limit?: number
  sort?: PlatformActivitySort
  order?: SortOrder
}

/**
 * Subset of svc-audit-log-api `Event` used by the per-user audit feed.
 */
export interface AuditLogEvent {
  id: string
  org_id?: string
  service_name?: string
  event_name?: string
  status_code?: number
  timestamp: string
  caller?: {
    user_email?: string
    user_id?: string
    trigger_type?: 'user' | 'api' | 'automation'
  }
  activity?: string
  /** request body / emitted detail; JSON string or object */
  detail?: string | Record<string, unknown>
  source_url?: string
  resource_id?: string
  resource_type?: string
  action?: string
  visibility?: 'customer' | 'internal'
}

export interface GetAuditLogsResponse {
  logs: AuditLogEvent[]
  total: number
}
